<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { ArgonLoadingButton } from "@/components";
import { useUserStore } from "@/_store/userStore.js";
import { vehicleStore } from "@/_store/vehicleStore";
import { useVuelidate } from "@vuelidate/core";
import { required, requiredIf, email, helpers } from "@vuelidate/validators";
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonPhoneInput from "@/components/ArgonPhoneInput.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import VehicleSelectWithAvatar from "@/components/VehicleSelectWithAvatar.vue";
import ModalBase from "@/components/modal/ModalBase.vue";
import AlertMsg from "@/components/AlertMessage.vue";
import { errorUtils } from "@/utils";

const props = defineProps({
  type: {
    type: String,
    default: "",
  },
});

const userStore = useUserStore();
const vehiclesStore = vehicleStore();

const rules = computed(() => {
  const vehicleRequired = requiredIf(() => data.value.type === "TTMM_APP");

  return {
    user: {
      required: helpers.withMessage("Email es requerido", required),
      email: helpers.withMessage("Ingrese un email valido", email),
    },
    vehicle_id: {
      required: helpers.withMessage("Vehículo es requerido", vehicleRequired),
    },
    phone: {
      required: helpers.withMessage("Teléfono es requerido", vehicleRequired),
      valid: helpers.withMessage(
        "Teléfono no es válido",
        (value) => !value || /^\+\d{7,18}$/.test(String(value).trim())
      ),
    },
  };
});
const data = ref({
  user: "",
  name: "",
  phone: "",
  vehicle_id: "",
  vehicle: {
    code: "",
    contact: {
      name: "",
    },
  },
  type: props.type || "",
});
const v$ = ref(useVuelidate(rules, data));
const isVehicleType = computed(() => data.value.type === "TTMM_APP");
const vehicleSelectKey = computed(
  () => `vehicle-select-${key.value}-${data.value.type}`
);

const vehicleOptions = computed(() => {
  const list = Array.isArray(vehiclesStore.activeVehicles)
    ? vehiclesStore.activeVehicles
    : [];

  return list
    .map((vehicle, index) => {
      return {
        value: vehicle?._id || vehicle?.id || `vehicle_${index}`,
        label: vehicle?.name || vehicle?.label || vehicle?.plate || "Vehículo",
        abbreviation: vehicle?.abbreviation || "",
        color: vehicle?.route_color_hex || vehicle?.color || "",
        raw: vehicle,
      };
    })
    .filter((vehicle) => Boolean(vehicle.value));
});

function onVehicleSelectedChange(option) {
  const vehicle = Array.isArray(option) ? option[0] : option;
  const rawVehicle = vehicle?.raw || {};

  data.value.vehicle_id = vehicle?.value || "";
  data.value.vehicle = {
    ...data.value.vehicle,
    ...rawVehicle,
    code:
      rawVehicle?.code ||
      rawVehicle?.vehicleCode ||
      vehicle?.abbreviation ||
      "",
    contact: {
      ...(data.value.vehicle?.contact || {}),
      ...(rawVehicle?.contact || {}),
      name: rawVehicle?.name || vehicle?.label || "",
    },
  };
}

function loadActiveVehicles() {
  return vehiclesStore.getActiveVehicles(new Date().toISOString().slice(0, 10));
}

onMounted(() => {
  loadActiveVehicles();
});

async function validate() {
  await v$.value.$validate();
  return v$.value.$errors;
}
const snackbar = ref(null);
const closeSnackbar = () => {
  snackbar.value = null;
};
function submit() {
  const resValidation = validate();
  resValidation.then((values) => {
    let message = "";
    for (const value of values) {
      message = `${message}<li>${value.$message}</li>`;
    }
    if (message != "") {
      snackbar.value = {
        title: "Error",
        message: `<ul>${message}</ul>`,
        type: "danger",
        color: "white",
      };
    } else {
      let contract = { user: data.value.user, type: data.value.type };
      if (data.value.type == "TTMM_APP") {
        contract.vehicle_id = data.value.vehicle_id;
        contract.name = String(data.value.name || "").trim();
        contract.phone = String(data.value.phone || "").trim();
      }
      userStore.newUser(contract);
    }
  });
}
let key = ref(1);
function cleanData() {
  data.value = {
    user: "",
    name: "",
    phone: "",
    vehicle_id: "",
    vehicle: {
      code: "",
      contact: {
        name: "",
      },
    },
    type: props.type || "",
  };
  v$.value.$reset();
  key.value++;
}
watch(
  () => data.value.type,
  (value) => {
    if (value === "TTMM_APP") return;
    data.value.vehicle_id = "";
    data.value.name = "";
    data.value.phone = "";
    data.value.vehicle = {
      code: "",
      contact: {
        name: "",
      },
    };
  }
);
watch(
  () => userStore.error,
  (value) => {
    if (value) {
      snackbar.value = {
        title: "Error",
        message:  errorUtils.getErrorMessageHtml(value),
        type: "danger",
        color: "white",
      };
    }
  }
);
const disableButton = computed({
  get() {
    return userStore.disableButton;
  },
});
</script>

<template>
  <modal-base ref="newUserModal" @hide="cleanData">
    <template #header>
      <h6 id="ModalLabel" class="modal-title">Nuevo</h6>
    </template>
    <template #body>
      <form class="border-0">
        <alert-msg title="" type="simple" class="mb-3">
          <span
            >Se enviará al nuevo usuario un correo electrónico con las
            credenciales de acceso al portal</span
          >
        </alert-msg>
        <div v-if="isVehicleType" class="row">
          <div class="col-2">
            <label class="col-form-label required">Vehículo</label>
          </div>
          <div class="col-10">
            <vehicle-select-with-avatar
              v-if="vehicleOptions.length > 0"
              id="new-user-vehicle"
              :key="vehicleSelectKey"
              v-model="data.vehicle_id"
              :options="vehicleOptions"
              :filterable="true"
              menu-width="320"
              :error="v$.vehicle_id.$error"
              @selected-change="onVehicleSelectedChange"
            />
          </div>
        </div>
        <div>
          <div class="row">
            <div class="col-2">
              <label class="col-form-label required">Email</label>
            </div>
            <div class="col-10">
              <argon-input
                id="email"
                v-model="data.user"
                :max-length="75"
                type="text"
                name="email"
                size="lg"
                :error="v$.user.$error"
              />
            </div>
          </div>
        </div>
        <div v-if="isVehicleType">
          <div class="row">
            <div class="col-2">
              <label class="col-form-label">Nombre</label>
            </div>
            <div class="col-10">
              <argon-input
                id="new-user-name"
                v-model="data.name"
                :max-length="75"
                type="text"
                name="name"
                size="lg"
              />
            </div>
          </div>
        </div>
        <div v-if="isVehicleType">
          <div class="row">
            <div class="col-2">
              <label class="col-form-label required">Teléfono</label>
            </div>
            <div class="col-10">
              <argon-phone-input
                id="new-user-phone"
                v-model="data.phone"
                :error="v$.phone.$error"
              />
            </div>
          </div>
        </div>
      </form>
    </template>
    <template #footer>
      <argon-loading-button
        class="btn btn-xs bg-gradient-primary"
        color="primary"
        variant="gradient"
        :label="disableButton ? 'Enviando...' : 'Enviar'"
        :disable-button="disableButton"
        @click="submit"
      >
      </argon-loading-button>
    </template>
    <div class="position-fixed top-1 end-1">
      <argon-snackbar
        v-if="snackbar"
        :title="snackbar.title"
        :description="snackbar.message"
        :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
        :color="snackbar.color"
        :close-handler="closeSnackbar"
      />
    </div>
  </modal-base>
</template>
<style lang="scss">
</style>
